import { useEffect, useRef, useState } from 'react';
import type { WorkStatsDTO, WorkStatsScope } from '@wberp/shared';
import { STATS_POLL_MS, workStatsStore } from './work-stats-store';
import './work-stats.css';

export function WorkStats({ scope, refreshKey, companyId, globalMode = false }: { scope: WorkStatsScope; refreshKey: number; companyId?: string; globalMode?: boolean }) {
  const [{ data, loading, error }, setState] = useState(() => workStatsStore.peek(scope, companyId));
  const lastRefresh = useRef(refreshKey);
  useEffect(() => {
    const unsubscribe = workStatsStore.subscribe(scope, () => setState(workStatsStore.peek(scope, companyId)), companyId);
    setState(workStatsStore.peek(scope, companyId));
    const refresh = () => { if (document.visibilityState === 'visible') void workStatsStore.load(scope, false, companyId); };
    refresh();
    const timer = window.setInterval(refresh, STATS_POLL_MS + Math.random() * 3000);
    document.addEventListener('visibilitychange', refresh);
    return () => { unsubscribe(); window.clearInterval(timer); document.removeEventListener('visibilitychange', refresh); };
  }, [scope, companyId]);
  useEffect(() => {
    if (lastRefresh.current === refreshKey) return;
    lastRefresh.current = refreshKey;
    void workStatsStore.load(scope, true, companyId);
  }, [scope, refreshKey, companyId]);

  const count = (...statuses: Array<keyof WorkStatsDTO['counts']>) => statuses.reduce((sum, status) => sum + (data?.counts[status] ?? 0), 0);
  // Quota waits have their own cards; keep them out of the generic waiting count.
  const quotaWait = scope === 'publish'
    ? (data?.waitReasons?.shopTotalQuota ?? 0) + (data?.waitReasons?.dailyQuota ?? 0)
    : 0;
  const items = [
    { label: scope === 'products' ? '商品总数' : scope === 'batches' ? '批次总数' : '任务总数', value: data?.total ?? 0, tone: 'total', hint: '当前账号权限内的全部记录' },
    ...(scope === 'products' ? [
      { label: '排队中', value: count('queued') },
      { label: '处理中', value: count('processing'), tone: 'active' },
      { label: '等待中', value: count('waiting') },
      { label: '已采集待处理', value: count('draft'), hint: 'WB 采集草稿，尚未完成预处理' },
      { label: '预处理完成', value: count('ready'), tone: 'success' },
      { label: '1688 链接不符合要求', value: count('unsellable'), tone: 'warning', hint: '包含 AI 判定不可售或规格轴暂不支持的商品' },
      { label: '失败', value: count('failed'), tone: 'danger' },
    ] : [
      ...(scope === 'batches' ? [
        { label: '处理中', value: count('queued', 'running', 'waiting'), tone: 'active', hint: '包含排队、执行和等待中的批次' },
      ] : [
        { label: '排队中', value: count('queued') },
        { label: '执行中', value: count('running'), tone: 'active' },
        { label: '等待中', value: Math.max(0, count('waiting') - quotaWait) },
        ...(scope === 'publish' ? [
          { label: '等待店铺剩余额度', value: data?.waitReasons?.shopTotalQuota ?? 0, tone: 'warning', hint: 'WB 店铺总建卡额度不足，释放或增加额度后自动继续' },
          { label: '等待明日发品额度', value: data?.waitReasons?.dailyQuota ?? 0, tone: 'warning', hint: 'WB 每日建卡额度已用完，明日额度重置后自动继续' },
        ] : []),
      ]),
      { label: '已完成', value: count('completed'), tone: 'success' },
      ...(scope === 'preprocess' ? [{ label: '1688 链接不符合要求', value: count('unsellable'), tone: 'warning', hint: '包含 AI 判定不可售或规格轴暂不支持的商品' }] : []),
      { label: '异常', value: count('failed', 'blocked'), tone: 'danger', hint: '包含失败和受阻的记录' },
      { label: scope === 'batches' ? '已取消' : '暂停／取消', value: count('paused', 'cancelled'), hint: scope === 'batches' ? '已取消的批次' : '包含已暂停和已取消的记录' },
    ]),
  ];
  return <section className="work-stats" aria-label="数量统计" aria-busy={loading}>
    <dl className="work-stats-values">{items.map(item => <div className={`work-stat work-stat-${item.tone ?? 'neutral'}${item.value === 0 ? ' work-stat-zero' : ''}`} key={item.label} title={item.hint}>
      <dt>{item.label}</dt><dd>{!data ? '—' : item.value.toLocaleString('zh-CN')}</dd>
    </div>)}</dl>
    <div className="work-stats-caption">
      <span>{globalMode ? '全局只读范围内全部记录' : '权限内全部记录'} · 不受下方筛选影响{scope === 'products' ? ' · 不含回收站' : ''}</span>
      <span role="status">{loading ? '统计读取中…' : error ? <>{data ? '刷新失败，保留缓存' : '统计暂不可用'} {!globalMode && <button className="copy-button" onClick={() => void workStatsStore.load(scope, true, companyId)}>重试</button>}</> : data ? `${data.mode === 'incremental' ? '增量缓存 · 每 5 分钟校准' : '快照缓存'} · 校准于 ${new Date(data.reconciledAt ?? data.asOf).toLocaleTimeString('zh-CN', { hour12: false })}` : ''}</span>
    </div>
  </section>;
}
